import { useCallback, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { TemplateContextSchema, TemplatePreviewResponse, TemplateValidateResponse } from '@/types';

interface UseReportTemplateParams {
  // When configId is provided, the per-config endpoints are used.
  // When omitted, draft-mode endpoints are used (create flow).
  configId?: string;
  teamId?: string | number;
  // Component IDs to scope context-schema and preview against in draft mode.
  // Ignored when configId is set (per-config endpoints derive these from the row).
  scopeComponentIds?: string[];
}

export function useReportTemplate(params: UseReportTemplateParams = {}) {
  const { configId, teamId, scopeComponentIds = [] } = params;

  const [contextSchema, setContextSchema] = useState<TemplateContextSchema | null>(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextError, setContextError] = useState<Error | null>(null);

  // The schema query depends on the comma-joined ids in draft mode. Caching the
  // serialised key avoids re-fetching when the array identity changes but
  // contents don't.
  const scopeKey = scopeComponentIds.join(',');

  const fetchContextSchema = useCallback(async () => {
    try {
      setContextLoading(true);
      setContextError(null);
      const scopeIds = scopeKey ? scopeKey.split(',') : [];
      const data = configId
        ? await vnextApiClient.getTemplateContextSchema(configId, teamId)
        : await vnextApiClient.getDraftTemplateContextSchema(scopeIds);
      setContextSchema(data);
      return data;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load template context');
      setContextError(error);
      Sentry.captureException(error);
      throw error;
    } finally {
      setContextLoading(false);
    }
  }, [configId, teamId, scopeKey]);

  useEffect(() => {
    fetchContextSchema().catch(() => {
      // Error already captured by hook state + Sentry
    });
  }, [fetchContextSchema]);

  const validate = useCallback(
    async (template: string): Promise<TemplateValidateResponse> => {
      try {
        return configId
          ? await vnextApiClient.validateTemplate(configId, template, teamId)
          : await vnextApiClient.validateDraftTemplate(template);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Validate failed');
        Sentry.captureException(error);
        throw error;
      }
    },
    [configId, teamId]
  );

  const preview = useCallback(
    async (template: string | null | undefined): Promise<TemplatePreviewResponse> => {
      try {
        if (configId) {
          return await vnextApiClient.previewTemplate(
            configId,
            template === undefined ? {} : { template },
            teamId
          );
        }
        return await vnextApiClient.previewDraftTemplate({
          template: template ?? undefined,
          scope_component_ids: scopeKey ? scopeKey.split(',') : undefined,
        });
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Preview failed');
        Sentry.captureException(error);
        throw error;
      }
    },
    [configId, teamId, scopeKey]
  );

  return {
    contextSchema,
    contextLoading,
    contextError,
    refreshContext: fetchContextSchema,
    validate,
    preview,
  };
}

// Helper hook for debouncing a `validate` call from the editor on keystrokes.
// Returns the latest validation result; null while still typing or before the
// first run. Skips empty templates.
export function useDebouncedValidate(
  template: string,
  validate: (t: string) => Promise<TemplateValidateResponse>,
  delayMs = 400
) {
  const [result, setResult] = useState<TemplateValidateResponse | null>(null);
  const [pending, setPending] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    if (!template.trim()) {
      setResult({ valid: true, errors: [] });
      setPending(false);
      return;
    }
    setPending(true);
    const id = ++seq.current;
    timer.current = setTimeout(async () => {
      try {
        const next = await validate(template);
        if (id === seq.current) {
          setResult(next);
        }
      } catch {
        // Network errors swallowed for the live-validate path; the user can
        // still hit Preview / Save which surface errors more visibly.
      } finally {
        if (id === seq.current) {
          setPending(false);
        }
      }
    }, delayMs);
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [template, validate, delayMs]);

  return { result, pending };
}
